/**
 * Uncapped count of events discarded by an event processor, per pageload.
 *
 * WHY A SECOND COUNTER. `instrumentEventProcessorDrops` reports at most 20 rows
 * per signature and 100 per pageload, and refuses preview and localhost
 * outright. That is right for a row-per-drop sink and wrong for attribution: a
 * 3000-event burst from one tab shows up as 20 rows, and a burst from a preview
 * deployment shows up as nothing. Sentry's `client_discard / event_processor`
 * counter has neither limit, so the two never reconcile.
 *
 * The tally has neither limit either. It costs one map increment per drop and
 * is shipped as one small beacon, so the count can be joined against
 * `stats_v2` instead of being estimated from capped rows.
 */

/** Distinct `processor:eventType` keys kept before the rest fold into `other`. */
export const MAX_TALLY_KEYS = 40;

export type ProcessorTallyCounts = Record<string, number>;

export interface ProcessorTally {
  record(processor: string, eventType: string | undefined): void;
  isEmpty(): boolean;
  /** Return the counts accumulated since the last drain, and reset them. */
  drain(): ProcessorTallyCounts;
}

export function createProcessorTally(): ProcessorTally {
  let counts = new Map<string, number>();

  return {
    record(processor, eventType) {
      // Sentry bills an untyped event as an error (`getDataCategoryByType`).
      const key = `${processor || 'unknown'}:${eventType || 'error'}`;
      const known = counts.get(key);
      if (known !== undefined) {
        counts.set(key, known + 1);
      } else if (counts.size < MAX_TALLY_KEYS) {
        counts.set(key, 1);
      } else {
        counts.set('other', (counts.get('other') ?? 0) + 1);
      }
    },
    isEmpty() {
      return counts.size === 0;
    },
    drain() {
      const out = Object.fromEntries(counts);
      counts = new Map();
      return out;
    },
  };
}

/** How often a long-lived tab ships its tally, besides on hide and unload. */
export const TALLY_FLUSH_INTERVAL_MS = 30_000;

export interface ProcessorTallyBeaconArgs {
  tally: ProcessorTally;
  endpoint: string;
  environment: string;
  getRoute: () => string;
  sendBeacon: (url: string, body: string) => boolean;
  addEventListener: (type: string, listener: () => void) => void;
  isHidden: () => boolean;
  setInterval: (fn: () => void, ms: number) => unknown;
}

/**
 * Ship the tally with `sendBeacon`, which survives the page unloading.
 *
 * Flushes on `pagehide`, on `visibilitychange` to hidden (the last event a
 * mobile browser reliably fires), and every 30 s for a tab left open. Each
 * flush drains, so the same drop is never sent twice. An empty tally sends
 * nothing. The body is a plain string so the request stays a simple one, with
 * no CORS preflight.
 */
export function installProcessorTallyBeacon(
  args: ProcessorTallyBeaconArgs,
): () => void {
  const { tally, endpoint, environment, getRoute, sendBeacon } = args;

  const flush = () => {
    if (tally.isEmpty()) return;
    try {
      const processors = tally.drain();
      sendBeacon(
        endpoint,
        JSON.stringify({ v: 1, environment, route: getRoute(), processors }),
      );
    } catch {
      // Telemetry only: a failed beacon loses one window of counts.
    }
  };

  args.addEventListener('pagehide', flush);
  args.addEventListener('visibilitychange', () => {
    if (args.isHidden()) flush();
  });
  args.setInterval(flush, TALLY_FLUSH_INTERVAL_MS);
  return flush;
}
